import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, SelectProps, Spin } from 'antd'
import { useState } from 'react'

import { useClassifierServiceGetInvestigationClassifierListEndpointKey } from 'src/api/queries'
import { ClassifierService, InvestigationTypeItemDto } from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { Select } from 'src/components'
import { useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

export const InvestigationTypesInfiniteSelect = ({ ...rest }: SelectProps) => {
  const { t, currentLang } = useLocales()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useClassifierServiceGetInvestigationClassifierListEndpointKey,
      debouncedSearch
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response =
        await ClassifierService.getInvestigationClassifierListEndpoint(
          pageParam,
          DEFAULT_PER_PAGE,
          debouncedSearch
        )

      return {
        data: response.data ?? [],
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: InvestigationTypeItemDto[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const selectOptions = [
    ...(data?.pages.flatMap(
      (page) =>
        page.data?.map((item: InvestigationTypeItemDto) => ({
          value: item?.id,
          label: item?.displayName?.[currentLang?.value]
        })) || []
    ) || [])
  ]

  const onClear = () => {
    setSearch('')
    rest.onClear?.()
  }

  return (
    <Select
      allowClear
      onSearch={(value) => setSearch(value)}
      onClear={onClear}
      filterOption={false}
      options={selectOptions}
      loading={isFetching}
      placeholder={t('laboratory_classifiers.investigation_type')}
      showSearch={true}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight)
          fetchNextPage()
      }}
      notFoundContent={
        rest.loading ? (
          <Flex justify="center">
            <Spin spinning />
          </Flex>
        ) : (
          <Empty />
        )
      }
      {...rest}
    />
  )
}
